/**
 * useStagingStylesFilter — native port of the legacy
 * `VirtualFurnitureStylesFilter.context.tsx`, rewired off `useStyleSelection` onto
 * the module working context + plain-JS room arrays. Derives the available room
 * types / removable options / per-room-type filtered styles + filter chips from
 * the static `VirtualFurnitureData` catalogue (React Query, `components/contexts/*`
 * — an ALLOWED import) and the active room's selections.
 *
 * Pure derivation over server state + the active room — no persisted-store write.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  VirtualFurnitureRoomRemovableOption,
  VirtualFurnitureRoomType,
  VirtualFurnitureStyleCategory
} from 'constants/virtualFurniture'
import type { InlineFilterItem } from 'components/common/InlineFilters'
import type { VirtualFurnitureStyleDTO } from 'models/virtualFurniture'
import { useVirtualFurnitureData } from 'components/contexts/VirtualFurnitureData.context'

import { useStagingWorkingState } from './StagingWorkingState.context'

export interface StagingStylesFilter {
  availableRoomTypes: VirtualFurnitureRoomType[]
  availableCategories: VirtualFurnitureStyleCategory[]
  availableRoomRemovableOptions: VirtualFurnitureRoomRemovableOption[]
  filteredStylesByRoomType: Partial<Record<VirtualFurnitureRoomType, VirtualFurnitureStyleDTO[]>>
  availableFiltersByRoomType: Record<string, InlineFilterItem<string>[]>
}

export function useStagingStylesFilter(): StagingStylesFilter {
  const { t } = useTranslation(['virtual_furniture_style_category'])
  const { virtualFurnitureStyles } = useVirtualFurnitureData()
  const { activeRoom } = useStagingWorkingState()

  return useMemo<StagingStylesFilter>(() => {
    const availableRoomTypes = new Set<VirtualFurnitureRoomType>()
    const availableCategories = new Set<VirtualFurnitureStyleCategory>()
    const availableRoomRemovableOptions = new Set<VirtualFurnitureRoomRemovableOption>(
      Object.values(VirtualFurnitureRoomRemovableOption)
    )
    const filteredStylesByRoomType: Partial<Record<VirtualFurnitureRoomType, VirtualFurnitureStyleDTO[]>> = {}
    const finalMappedFilters: Record<string, InlineFilterItem<string>[]> = {}

    if (activeRoom) {
      const { propertyType, roomTypes, styleCategory } = activeRoom
      const filters: Partial<Record<VirtualFurnitureRoomType, Record<string, InlineFilterItem<string>>>> = {}

      for (const style of virtualFurnitureStyles) {
        // Ignore styles from other propertyTypes than the active room's.
        if (!!propertyType && propertyType !== style.propertyType) continue

        const newFilters = { ...filters[style.roomType] }
        newFilters[style.style] = newFilters[style.style]
          ? { ...newFilters[style.style], count: (newFilters?.[style.style]?.count || 0) + 1 }
          : { label: t(style.style), value: style.style, count: 1 }
        filters[style.roomType] = newFilters

        availableRoomTypes.add(style.roomType)

        // Only the active room's selected types contribute styles/categories.
        if (!roomTypes.includes(style.roomType)) continue

        if (!filteredStylesByRoomType[style.roomType]) filteredStylesByRoomType[style.roomType] = []
        availableCategories.add(style.style)

        if (!!styleCategory && styleCategory !== style.style) continue

        filteredStylesByRoomType[style.roomType] = [...(filteredStylesByRoomType[style.roomType] || []), style]
      }

      for (const [roomType, value] of Object.entries(filters)) {
        finalMappedFilters[roomType] = Object.values(value)
      }
    }

    return {
      availableRoomTypes: Array.from(availableRoomTypes),
      availableCategories: Array.from(availableCategories),
      availableRoomRemovableOptions: Array.from(availableRoomRemovableOptions),
      filteredStylesByRoomType,
      availableFiltersByRoomType: finalMappedFilters,
    }
  }, [activeRoom, virtualFurnitureStyles, t])
}
